import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Loader2, ArrowLeft, CreditCard } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useCart } from "@/store/useCart";
import { formatPrice } from "@/lib/utils";
import { api } from "@/lib/api";

const bdPhoneRegex = /^(\+88)?01[3-9]\d{8}$/;

const checkoutSchema = z.object({
    firstName: z.string().min(2, "Name is too short"),
    lastName: z.string().optional(),
    phone: z.string().regex(bdPhoneRegex, "Invalid Bangladesh phone number"),
    address: z.string().min(5, "Address is too short"),
    city: z.string().min(2, "City is required"),
    paymentMethod: z.enum(["COD", "BKASH"]).default("COD"),
});

type CheckoutFormValues = z.infer<typeof checkoutSchema>;

export function CheckoutPage() {
    const { items, getTotalPrice, clearCart } = useCart();
    const navigate = useNavigate();

    const form = useForm({
        resolver: zodResolver(checkoutSchema),
        defaultValues: {
            firstName: "",
            lastName: "",
            phone: "",
            address: "",
            city: "",
            paymentMethod: "COD",
        },
    });

    const onSubmit = async (data: CheckoutFormValues) => {
        try {
            const orderPayload = {
                customer: {
                    name: `${data.firstName} ${data.lastName || ''}`.trim(),
                    phone: data.phone,
                    email: "",
                    address: data.address, // API expects address in customer
                    city: data.city,       // API expects city in customer
                    // Remove country if not in API schema or add if expected, 
                    // but 'customerSchema' in orders.ts does not have 'country'.
                },
                // items checks out
                items: items.map(item => ({
                    productId: item.id,
                    quantity: item.quantity,
                })),
                paymentMethod: data.paymentMethod.toLowerCase(), // 'cod' vs 'COD'
                // source defaults to 'website'
            };

            const res = await api.post<any>('/orders', orderPayload);

            clearCart();

            navigate("/success", { state: { orderId: res.id, phone: data.phone } });

        } catch (error: any) {
            console.error("Checkout Error:", error);
            let message = "Failed to place order. Please try again.";

            // Check if it's our APIError structure
            if (error.data && error.data.error) {
                message = error.data.error.message || message;
                if (error.data.error.details && error.data.error.details.errors) {
                    // If multiple validation errors (e.g. stock), join them
                    message += `: ${error.data.error.details.errors.join(', ')}`;
                }
            } else if (error.message) {
                message = error.message;
            }

            form.setError("root", { message });
        }
    };

    if (items.length === 0) {
        return (
            <div className="container py-20 text-center">
                <h1 className="text-2xl font-bold mb-4">Your cart is empty</h1>
                <Button onClick={() => navigate("/")}>Go Shopping</Button>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-8 max-w-6xl">
            <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6 pl-0 hover:bg-transparent">
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
            </Button>

            <div className="grid gap-8 lg:grid-cols-2">
                {/* Form Section */}
                <div>
                    <div className="mb-8">
                        <h1 className="text-2xl font-bold">Checkout</h1>
                        <p className="text-muted-foreground">Complete your order.</p>
                    </div>

                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                            <div className="grid gap-4 sm:grid-cols-2">
                                <FormField control={form.control} name="firstName" render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>First Name</FormLabel>
                                        <FormControl><Input placeholder="John" {...field} /></FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )} />
                                <FormField control={form.control} name="lastName" render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Last Name</FormLabel>
                                        <FormControl><Input placeholder="Doe" {...field} /></FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )} />
                            </div>

                            <FormField control={form.control} name="phone" render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Phone Number</FormLabel>
                                    <FormControl><Input placeholder="017..." {...field} /></FormControl>
                                    <FormMessage />
                                </FormItem>
                            )} />

                            <FormField control={form.control} name="address" render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Address</FormLabel>
                                    <FormControl><Input placeholder="House 123, Road 4..." {...field} /></FormControl>
                                    <FormMessage />
                                </FormItem>
                            )} />

                            <FormField control={form.control} name="city" render={({ field }) => (
                                <FormItem>
                                    <FormLabel>City</FormLabel>
                                    <FormControl><Input placeholder="Dhaka" {...field} /></FormControl>
                                    <FormMessage />
                                </FormItem>
                            )} />

                            <div className="rounded-lg border p-4">
                                <h3 className="font-semibold mb-3 flex items-center gap-2"><CreditCard className="h-4 w-4" /> Payment Method</h3>
                                <div className="flex items-center space-x-2">
                                    <div className="h-4 w-4 rounded-full bg-primary" />
                                    <span>Cash on Delivery (COD)</span>
                                </div>
                                <p className="text-sm text-muted-foreground mt-1 ml-6">Pay when you receive your order.</p>
                            </div>

                            <Button type="submit" className="w-full" size="lg" disabled={form.formState.isSubmitting}>
                                {form.formState.isSubmitting ? (
                                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Processing...</>
                                ) : (
                                    "Place Order"
                                )}
                            </Button>
                            {form.formState.errors.root && (
                                <p className="text-destructive text-sm text-center">{form.formState.errors.root.message}</p>
                            )}
                        </form>
                    </Form>
                </div>

                {/* Summary Section */}
                <div className="lg:pl-8">
                    <div className="rounded-xl border bg-card text-card-foreground shadow-sm p-6 sticky top-24">
                        <h2 className="font-semibold text-lg mb-4">Order Summary</h2>
                        <ul className="grid gap-4 mb-6">
                            {items.map((item) => (
                                <li key={item.id} className="flex items-center gap-4">
                                    <div className="h-16 w-16 overflow-hidden rounded bg-muted">
                                        {item.image && <img src={item.image} alt={item.name} className="h-full w-full object-cover" />}
                                    </div>
                                    <div className="flex-1">
                                        <h4 className="font-medium text-sm line-clamp-2">{item.name}</h4>
                                        <div className="text-sm text-muted-foreground">Qty: {item.quantity}</div>
                                    </div>
                                    <div className="font-medium text-sm">{formatPrice(item.price * item.quantity)}</div>
                                </li>
                            ))}
                        </ul>

                        <div className="space-y-2 border-t pt-4">
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">Subtotal</span>
                                <span>{formatPrice(getTotalPrice())}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">Shipping</span>
                                <span className="text-muted-foreground text-sm">Calculated later</span>
                            </div>
                            <div className="flex justify-between border-t pt-4 font-bold text-lg">
                                <span>Total</span>
                                <span>{formatPrice(getTotalPrice())}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
